import React, { useCallback, useEffect, useState } from 'react'
import { NetworkName } from '@components/Tokens/TokenSelector/NetworkName'
import { TokenImage } from '@components/Tokens/TokenSelector/TokenImage'
import { Token } from '@components/Tokens/TokenSelector/tokenSchemas'
import { Box, BoxProps, Icon, Text, Tooltip } from '@ui'
import { useTokenMetadataForChainId } from 'api/lib/collectionMetadata'
import useCopyToClipboard from 'hooks/useCopyToClipboard'
import { Entitlements } from 'hooks/useEntitlements'
import { Address } from 'use-towns-client'

type Props = {
    entitlements: Entitlements
}

export const EntitlementsDisplayV2 = (props: Props) => {
    const { entitlements } = props
    const tokensGatedBy = entitlements.tokens
    const usersGatedBy = entitlements.users
    const ethBalanceGatedBy = entitlements.ethBalance

    const hasUsers = usersGatedBy.length > 0
    const hasEthBalance = ethBalanceGatedBy !== ''

    if (!entitlements.hasEntitlements) {
        return null
    }

    const reservedSlots = (hasUsers ? 1 : 0) + (hasEthBalance ? 1 : 0)
    const maxTokenSlots = 5 - reservedSlots

    const tokens = Array.from(
        tokensGatedBy
            .reduce((map, token) => map.set(token.data.address, token), new Map())
            .values(),
    ).slice(0, maxTokenSlots)

    const icons: React.JSX.Element[] = []

    if (hasUsers) {
        icons.push(<UsersIcon key="users" />)
    }

    tokens.forEach((token, index) => {
        icons.push(
            <TokenIcon
                size="x2.5"
                key={`token-${token.data.address}-${token.chainId}`}
                token={token}
            />,
        )
    })

    if (hasEthBalance) {
        icons.push(<EthBalanceIcon key="eth" />)
    }

    return (
        <Box
            horizontal
            alignSelf="start"
            gap="xs"
            alignItems="center"
            justifyContent="center"
            height="x3"
        >
            <Box horizontal position="relative" gap="xxs">
                {icons}
            </Box>
        </Box>
    )
}

const TokenIcon = ({ token, size }: { token: Token; size: BoxProps['width'] }) => (
    <SelectedToken
        contractAddress={token.data.address as Address}
        chainId={token.chainId}
        size={size}
    />
)

export function SelectedToken({
    contractAddress,
    chainId,
    size = 'x3',
    ...boxProps
}: {
    contractAddress: string
    chainId: number
    size?: BoxProps['width']
} & Omit<BoxProps, 'size'>) {
    const { data: tokenDataWithChainId } = useTokenMetadataForChainId(contractAddress, chainId)
    const [copied, setCopied] = useState(false)
    const [, copy] = useCopyToClipboard()
    const onCopy = useCallback(() => {
        if (!tokenDataWithChainId?.data.address) {
            return
        }

        setCopied(true)
        copy(tokenDataWithChainId?.data.address)
    }, [copy, tokenDataWithChainId?.data.address])

    useEffect(() => {
        if (copied) {
            setTimeout(() => {
                setCopied(false)
            }, 1000)
        }
    }, [copied])
    return (
        <Box
            cursor="pointer"
            tooltip={
                <Tooltip gap="sm">
                    <Box horizontal gap="xs" alignItems="center">
                        <Text size="sm">{tokenDataWithChainId?.data.label}</Text>
                        <Text size="sm">·</Text>
                        <Box>
                            <NetworkName chainId={chainId} size="sm" color="initial" />
                        </Box>
                    </Box>
                    <Text size="xs" color="gray2">
                        {tokenDataWithChainId?.data.address}
                    </Text>
                </Tooltip>
            }
            onClick={onCopy}
        >
            {copied ? (
                <Box centerContent width={size} height={size}>
                    <Icon type="check" size="square_xxs" color="positive" />
                </Box>
            ) : tokenDataWithChainId ? (
                <TokenImage
                    imgSrc={tokenDataWithChainId.data?.imgSrc}
                    width={size}
                    background="level3"
                />
            ) : (
                <Box
                    centerContent
                    aspectRatio="1/1"
                    background="level4"
                    borderRadius="xs"
                    width={size}
                    {...boxProps}
                />
            )}
        </Box>
    )
}

const UsersIcon = () => (
    <Box tooltip="Specific wallet addresses" background="level2" borderRadius="xs">
        <Icon type="wallet" size="square_sm" />
    </Box>
)

const EthBalanceIcon = () => (
    <Box tooltip="ETH Balance requirement" background="level2" borderRadius="xs">
        <Icon type="eth" size="square_sm" />
    </Box>
)
